import { useT } from "../../../core/i18n";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import Toasts from "../../../core/common/toast";
import { getAttendanceSummary } from "../../../core/data/redux/employeesAttendanceSlice";
import { getMyProfile } from "../../../core/data/redux/employeesSlice";
import AttendanceDetailsModel from "../../../core/modals/attendanceDetailsModel";
import { all_routes } from "../../router/all_routes";
import { DatePicker } from "antd";
import { ConfigProvider } from "antd";


const TeamShiftHistory = () => {
  const { t } = useT();
  const [currentItem, setCurrentItem] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);

  const dispatch = useDispatch();
  const reportingEmployeesState = useSelector(
    (state: any) => state.reportingEmployeesState
  );

  const employeesState = useSelector((state: any) => state.employeesState);
  const employeesAttendanceState = useSelector(
    (state: any) => state.employeesAttendanceState
  );
  const user = employeesState?.data?.data || null;

  const [dataList, setDataList] = useState<any[]>([]);
  //console.log("employeesAttendanceState", employeesAttendanceState?.summary);
  const [query, setQuery] = useState<string>("");
  const [filterEmpId, setFilterEmpId] = useState<string>("");
  const [fromDate, setFromDate] = useState<dayjs.Dayjs | null>(null);
  const [toDate, setToDate] = useState<dayjs.Dayjs | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10,
  });

  const showLocation = (record: any) => {
    setCurrentItem(record);
    setShowModal(true);
  };

  const [attendanceSummary, setAttendanceSummary] = useState({
    present: 0,
    absent: 0,
    half_day: 0,
    late_coming: 0,
    early_leaving: 0,
    holiday: 0,
  });

  const routes = all_routes;

  // Debounce function
  function debounce(func: (...args: any[]) => void, delay: number) {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  }

  // Get employee profile on mount
  const fetchProfileRecords = () => {
    dispatch(getMyProfile({}));
  };

  useEffect(() => {
    fetchProfileRecords();
  }, []);

  // Fetch attendance records
  const fetchRecords = (
    pagination = { current: 1, pageSize: 10 },
    empId: string = ""
  ) => {
    const finalEmpId = empId || user?.empId || "";
    if (!finalEmpId) return;
    dispatch(
      getAttendanceSummary({
        page: pagination.current,
        limit: pagination.pageSize,
        is_verified: "VERIFIED",
        //empId: finalEmpId,
      })
    );
  };

  useEffect(() => {
    if (user?.empId) {
      fetchRecords(pagination);
    }
  }, [user?.empId]);

  useEffect(() => {
    if (
      employeesAttendanceState.loading === "fulfilled" &&
      employeesAttendanceState.action === "assign"
    ) {
      fetchRecords(pagination, user?.empId);
    } else if (
      employeesAttendanceState.loading === "fulfilled" &&
      employeesAttendanceState.action === "list"
    ) {
      const result = employeesAttendanceState?.summary?.data || null;

      if (result?.data && result.data.length > 0) {
        const employees = result.data;
        setDataList(employees);
        setPagination({
          ...pagination,
          total: result.total || employees.length,
          current: result.page || 1,
          pageSize: result.limit || 10,
        });
      } else {
        setDataList([]);
      }
    }
  }, [employeesAttendanceState.loading]);

  // Debounced search
  const debouncedSearch = useRef(
    debounce((value: string) => {
      const trimmed = value.trim();
      if (trimmed === "") {
        setQuery("");
        fetchRecords(pagination, ""); // show all
        return;
      }
      if (trimmed.length < 4) return; // wait for 4 digits
      setQuery(trimmed);
      fetchRecords(pagination, trimmed);
    }, 500)
  ).current;

  const handleSearch = (value: string) => {
    debouncedSearch(value);
  };

  const handlePagination = (pagination: any) => {
    setPagination({
      current: pagination.page,
      pageSize: pagination.pageSize,
      total: pagination.total || 10,
    });
    fetchRecords(pagination, query);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      PRESENT: "#049123",
      ABSENT: "#C0282C",
      HALF_DAY: "#FFA500",
      LATE_COMING: "#B09BFB",
      EARLY_LEAVING: "#FFFF00",
      HOLIDAY: "#969696",
    };
    return colors[status] || "#ccc";
  };

  const attendanceColumns = [
    {
      title: t("sr_no"),
      dataIndex: "id",
      width: 80,
      render: (text: string, record: any, index: number) =>
        index + 1 + (pagination.current - 1) * pagination.pageSize,
    },
    {
      title: t("emp_id"),
      dataIndex: "empId",
      render: (empId: string) => parseInt(empId, 10),
    },
    {
      title: t("check_in"),
      dataIndex: "check_in_time",
      render: (text: string, record: any) =>
        text ? dayjs(record.check_in_time).format("DD-MM-YYYY hh:mm A") : "-",
    },
    {
      title: t("check_in_location"),
      dataIndex: "check_in_latitude",
      render: (text: string, record: any) => (
        <div>
          <Link
            to={"#"}
            className="text-decoration-none"
            onClick={() => showLocation(record)}
          >
            <div>
              <b>Latitude</b>: {record.check_in_latitude}
            </div>
            <div>
              <b>Longitude</b>: {record.check_in_longitude}
            </div>
          </Link>
        </div>
      ),
    },
    {
      title: t("check_out"),
      dataIndex: "check_out_time",
      render: (text: string, record: any) =>
        text
          ? dayjs(record.check_out_time).format("DD-MM-YYYY hh:mm A")
          : "-",
    },
    {
      title: t("working_hours"),
      dataIndex: "working_hours",
      render: (text: string) => text || "-",
    },
    {
      title: t("work_type"),
      dataIndex: "attendance_work_type",
      render: (text: any) => <span>{text || "-"}</span>,
    },
    {
      title: t("wing"),
      dataIndex: "employee",
      render: (employee: any) =>
        employee?.details?.sap_department_designation_wings?.wing_name
          ? `${employee?.details?.sap_department_designation_wings?.wing_name}`
          : "",
    },
    {
      title: t("department"),
      dataIndex: "employee",
      render: (employee: any) =>
        employee?.details?.sap_department_designation_wings?.department_name
          ? `${employee?.details?.sap_department_designation_wings?.department_name}`
          : "",
    },
    {
      title: t("designation"),
      dataIndex: "employee",
      render: (employee: any) =>
        employee?.details?.sap_department_designation_wings?.designation_name
          ? `${employee?.details?.sap_department_designation_wings?.designation_name}`
          : "",
    },
    {
      title: t("attendance_status"),
      dataIndex: "attendance_status",
      render: (text: string) => (
        <span
          style={{
            backgroundColor: getStatusColor(text),
            color: "#fff",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "12px",
          }}
        >
          {text?.replace(/_/g, " ")?.replace(/\b\w/g, (c) => c.toUpperCase())}
        </span>
      ),
    },
    {
      title: "Verification",
      dataIndex: "is_verified",
    },
  ];

  const employeeStats = [
    {
      title: t("present"),
      count: attendanceSummary.present,
      color: "#22c55e", // Green
      icon: "ti ti-user-check",
      badgeBg: "#dcfce7", // green-100
      badgeColor: "#166534", // green-700
    },
    {
      title: t("absent"),
      count: attendanceSummary.absent,
      color: "#ef4444", // Red
      icon: "ti ti-user-x",
      badgeBg: "#fee2e2", // red-100
      badgeColor: "#991b1b", // red-700
    },
    {
      title: t("half_day"),
      count: attendanceSummary.half_day,
      color: "#f59e0b",
      icon: "ti ti-user-minus",
      badgeBg: "#fef3c7", // amber-100
      badgeColor: "#78350f", // amber-800
    },
    {
      title: t("late_coming"),
      count: attendanceSummary.late_coming,
      color: "#8b5cf6", // Violet
      icon: "ti ti-clock-hour-9",
      badgeBg: "#ede9fe", // violet-100
      badgeColor: "#5b21b6", // violet-800
    },
    {
      title: t("early_leaving"),
      count: attendanceSummary.early_leaving,
      color: "#eab308", // Yellow
      icon: "ti ti-door-exit",
      badgeBg: "#fef9c3", // yellow-100
      badgeColor: "#92400e", // yellow-800
    },
    {
      title: t("holidays"),
      count: attendanceSummary.holiday,
      color: "#6b7280", // Gray
      icon: "ti ti-calendar-star",
      badgeBg: "#e5e7eb", // gray-100
      badgeColor: "#1f2937", // gray-800
    },
  ];

  return (
    <div>
      <>
        <Toasts
          show={reportingEmployeesState.loading === "rejected"}
          message={reportingEmployeesState.error}
          title={"Failed"}
          type={"danger"}
        />
        <Toasts
          show={
            reportingEmployeesState.loading === "fulfilled" &&
            reportingEmployeesState.action !== "list"
          }
          message={reportingEmployeesState?.data?.message}
          title={"Success"}
          type={"success"}
        />
        <div className="page-wrapper">
          <div className="content">
            <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">{t("attendance_summary")}</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={routes.adminDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="#">Employees</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">{t("attendance_summary")}</li>
                  </ol>
                </nav>
              </div>
            </div>
            {/* ✅ Attendance summary cards */}
            <div className="row">
              {employeeStats.map((stat, index) => (
                <div className="col-md-6 col-lg-3 mb-4" key={index}>
                  <div
                    className="d-flex justify-content-between align-items-center p-3 rounded shadow-sm"
                    style={{
                      backgroundColor: "#fff",
                      border: "1px solid #f1f1f1",
                    }}
                  >
                    {/* Left: Icon + Title + Count */}
                    <div className="d-flex align-items-center gap-3">
                      {/* Icon */}
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: 48,
                          height: 48,
                          backgroundColor: stat.color,
                          color: "#fff",
                          fontSize: 22,
                        }}
                      >
                        <i className={stat.icon}></i>
                      </div>

                      {/* Title & Count */}
                      <div>
                        <div
                          className="fw-semibold mb-1"
                          style={{ fontSize: 14, color: "#4b5563" }}
                        >
                          {stat.title}
                        </div>
                        <div
                          className="fw-bold"
                          style={{ fontSize: 20, color: "#0f172a" }}
                        >
                          {stat.count}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                <h4 className="mb-4 mt-3">{t("attendance_summary")}</h4>
                {/* Filters Row */}
                <div className="d-flex align-items-center gap-2 mb-4 mt-3" style={{ flexWrap: 'nowrap' }}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    style={{
                      maxWidth: '200px',
                      height: '31px',
                      lineHeight: '31px',
                      position: 'relative',
                      top: "-3px",
                      padding: '0.25rem 0.5rem'
                    }}
                    placeholder={t("search_by_emp_id")}
                    value={filterEmpId}
                    onChange={(e) => setFilterEmpId(e.target.value)}
                  />
                  <ConfigProvider>
                    <DatePicker
                      size="small"
                      style={{ maxWidth: '150px', height: '31px' }}
                      placeholder={t("from_date")}
                      format="DD-MM-YYYY"
                      value={fromDate}
                      onChange={(date) => setFromDate(date)}
                    />
                  </ConfigProvider>
                  <ConfigProvider>
                    <DatePicker
                      size="small"
                      style={{ maxWidth: '150px', height: '31px' }}
                      placeholder={t("to_date")}
                      format="DD-MM-YYYY"
                      value={toDate}
                      onChange={(date) => setToDate(date)}
                      disabledDate={(current) => {
                        if (!fromDate) return false;
                        return current && current < fromDate.startOf("day");
                      }}
                    />
                  </ConfigProvider>
                  <div className="d-flex gap-2 " style={{ position: "relative", top: "-2px" }}>
                    <button
                      type="button"
                      className="btn btn-info btn-sm"
                      style={{ height: '31px', lineHeight: '1', padding: '0.25rem 0.75rem' }}
                      onClick={() => {
                        // TODO: Backend integration pending
                        console.log("Filter:", { filterEmpId, fromDate, toDate });
                      }}
                    >
                      Search
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      style={{ height: '31px', lineHeight: '1', padding: '0.25rem 0.75rem' }}
                      onClick={() => {
                        setFilterEmpId("");
                        setFromDate(null);
                        setToDate(null);
                      }}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body p-0 py-3">
                {/* 👇 Attendance Table */}
                <Table
                  columns={attendanceColumns}
                  dataSource={dataList}
                  Selection={false}
                  paginationConfig={pagination}
                  handlePagination={handlePagination}
                  loading={reportingEmployeesState.loading === "pending"}
                  handleSearch={handleSearch}
                />
              </div>
            </div>
            <div className="row align-items-center">
              <div className="col-md-12">
                <div className="datatable-paginate mt-4" />
              </div>
            </div>
          </div>
        </div>
      </>
      {currentItem && (
        <AttendanceDetailsModel
          show={showModal}
          item={currentItem}
          close={() => {
            setShowModal(false);
            setCurrentItem(null);
          }}
        />
      )}
    </div>
  );
};

export default TeamShiftHistory;
